import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Save,
  Loader2,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  TestTube,
  Trash2,
  Eye,
  EyeOff,
  RefreshCw,
  Zap,
  Workflow,
  PhoneCall,
  History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientTwilioConfigPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [showAuthToken, setShowAuthToken] = useState(false);
  
  const [formData, setFormData] = useState({
    account_sid: '',
    auth_token: '',
    workspace_sid: '',
    workflow_sid: '',
    studio_flow_sid: '',
    flex_phone_number: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    if (!token) {
      navigate('/client-portal');
      return;
    }
    fetchData();
  }, [navigate]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('client_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statusRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/client-portal/twilio/status`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/client-portal/twilio/events?limit=20`, { headers: getAuthHeaders() })
      ]);
      
      setStatus(statusRes.data);
      setEvents(eventsRes.data);
      
      // Pre-fill form if config exists
      if (statusRes.data.config) {
        setFormData({
          account_sid: statusRes.data.config.account_sid || '',
          auth_token: '', // Never pre-fill auth token for security
          workspace_sid: statusRes.data.config.workspace_sid || '',
          workflow_sid: statusRes.data.config.workflow_sid || '',
          studio_flow_sid: statusRes.data.config.studio_flow_sid || '',
          flex_phone_number: statusRes.data.config.flex_phone_number || ''
        });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/client-portal');
      } else {
        toast.error('Failed to load Twilio status');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.account_sid) {
      toast.error('Account SID is required');
      return;
    }
    if (!formData.auth_token && !status?.is_configured) {
      toast.error('Auth Token is required');
      return;
    }
    
    setSaving(true);
    try {
      const dataToSend = { ...formData };
      // Only send auth_token if it's been changed
      if (!formData.auth_token) {
        delete dataToSend.auth_token;
      }
      
      await axios.put(
        `${API_URL}/api/client-portal/twilio/config`,
        dataToSend,
        { headers: getAuthHeaders() }
      );
      toast.success('Twilio configuration saved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/client-portal/twilio/test`,
        {},
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(`Connected to: ${response.data.account_friendly_name}`);
      } else {
        toast.error(`Connection failed: ${response.data.error}`);
      }
      fetchData();
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your Twilio configuration?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/client-portal/twilio/config`,
        { headers: getAuthHeaders() }
      );
      toast.success('Twilio configuration deleted');
      setFormData({
        account_sid: '',
        auth_token: '',
        workspace_sid: '',
        workflow_sid: '',
        studio_flow_sid: '',
        flex_phone_number: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete configuration');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  // Not available for this client
  if (!status?.available) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="twilio-config-unavailable">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/client-portal/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">Twilio Flex Integration</h1>
              <p className="text-sm text-muted-foreground">Configure your contact center</p>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Twilio Flex Not Available</h2>
              <p className="text-muted-foreground mb-4">
                {!status?.global_enabled 
                  ? "Twilio Flex integration is not enabled on this platform."
                  : "Twilio Flex has not been enabled for your account. Please contact your administrator."}
              </p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-twilio-config-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/client-portal/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <Phone className="w-5 h-5 text-red-500" />
                Twilio Flex Integration
              </h1>
              <p className="text-sm text-muted-foreground">Configure your contact center and IVR</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {status?.is_configured && (
              <>
                <Button variant="outline" onClick={handleTest} disabled={testing}>
                  {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Test Connection
                </Button>
                <Button variant="outline" onClick={handleDelete} className="text-red-600">
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Configuration
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Integration Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                {status?.is_configured ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-gray-400" />
                )}
                <span>{status?.is_configured ? 'Configured' : 'Not Configured'}</span>
              </div>
              
              {status?.config?.connection_tested && (
                <div className="flex items-center gap-2">
                  {status.config.last_test_result === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span>
                    Last test: {status.config.last_test_result}
                    {status.config.last_test_at && ` (${formatDate(status.config.last_test_at)})`}
                  </span>
                </div>
              )}
              
              {status?.config?.account_friendly_name && (
                <Badge variant="outline">{status.config.account_friendly_name}</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Credentials Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Twilio Account Credentials</CardTitle>
            <CardDescription>
              Find these in your <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">Twilio Console</a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Account SID *</Label>
                <Input
                  value={formData.account_sid}
                  onChange={(e) => setFormData({ ...formData, account_sid: e.target.value })}
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">Found on your Twilio Console dashboard</p>
              </div>
              <div>
                <Label>Auth Token *</Label>
                <div className="relative">
                  <Input
                    type={showAuthToken ? 'text' : 'password'}
                    value={formData.auth_token}
                    onChange={(e) => setFormData({ ...formData, auth_token: e.target.value })}
                    placeholder={status?.is_configured ? '••••••••••••••••' : 'Enter your Auth Token'}
                    className="font-mono pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowAuthToken(!showAuthToken)}
                  >
                    {showAuthToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {status?.is_configured ? 'Leave blank to keep existing token' : 'Found on your Twilio Console dashboard'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Flex Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              Flex TaskRouter Configuration
            </CardTitle>
            <CardDescription>
              For routing leads to agents. Find these in Flex → TaskRouter
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Workspace SID</Label>
                <Input
                  value={formData.workspace_sid}
                  onChange={(e) => setFormData({ ...formData, workspace_sid: e.target.value })}
                  placeholder="WSxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">TaskRouter workspace for your agents</p>
              </div>
              <div>
                <Label>Workflow SID</Label>
                <Input
                  value={formData.workflow_sid}
                  onChange={(e) => setFormData({ ...formData, workflow_sid: e.target.value })}
                  placeholder="WWxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">Routing workflow for task assignment</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Studio Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Workflow className="w-4 h-4 text-purple-500" />
              Studio Flow Configuration
            </CardTitle>
            <CardDescription>
              For IVR and automated lead qualification
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Studio Flow SID</Label>
                <Input
                  value={formData.studio_flow_sid}
                  onChange={(e) => setFormData({ ...formData, studio_flow_sid: e.target.value })}
                  placeholder="FWxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground mt-1">Your IVR/qualification flow</p>
              </div>
              <div>
                <Label>Flex Phone Number</Label>
                <Input
                  value={formData.flex_phone_number}
                  onChange={(e) => setFormData({ ...formData, flex_phone_number: e.target.value })}
                  placeholder="+1234567890"
                />
                <p className="text-xs text-muted-foreground mt-1">Your Twilio phone number for calls</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Events Card */}
        {events.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {events.slice(0, 10).map((event) => (
                    <TableRow key={event.id}>
                      <TableCell className="text-sm">{formatDate(event.timestamp)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{event.event_type.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {event.details?.task_sid && `Task: ${event.details.task_sid.slice(-8)}`}
                        {event.details?.call_sid && `Call: ${event.details.call_sid.slice(-8)}`}
                        {event.details?.execution_sid && `Flow: ${event.details.execution_sid.slice(-8)}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Help Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <h3 className="font-medium text-blue-800 mb-2">Need Help Setting Up?</h3>
            <div className="text-sm text-blue-700 space-y-1">
              <p>1. <strong>Account SID & Auth Token</strong>: Found on your <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio Console</a> dashboard</p>
              <p>2. <strong>Workspace SID</strong>: Go to Flex → TaskRouter → Workspaces</p>
              <p>3. <strong>Workflow SID</strong>: Go to Flex → TaskRouter → Workflows</p>
              <p>4. <strong>Studio Flow SID</strong>: Go to Studio → Flows → Select your flow</p>
              <p>5. <strong>Phone Number</strong>: Go to Phone Numbers → Active Numbers</p>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
